/**
 * Spec 082 FR-F1 — paginated table of pending candidate rules, filterable
 * by author + confidence. Clicking a row navigates to the detail page.
 *
 * Spec 092 — multi-select + bulk-reject. New per-row checkbox column,
 * header checkbox with indeterminate state (select-all-on-page only),
 * sticky action bar visible when at least one row is selected, and a
 * confirm modal with an optional reason textarea. Admin role gating is
 * enforced on both the button (disabled w/ tooltip via `useAuth().hasRole`)
 * and the backend (`requireRuleAdmin`).
 *
 * `data-testid` attributes wire the Playwright gates 082.J and 092.A.4.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/ui/Icon.js';
import {
  bulkRejectCandidates,
  listCandidates,
  listSources,
  type CandidateRule,
  type AuthorConfidence,
  type CandidateReviewStatus,
  type MediumSource,
} from '../../api/ruleIngestion.js';
import { useAuth } from '../../lib/auth-context.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RescanCandidatesButton } from '../../components/RescanCandidatesButton.js';
import { ClusterCandidatesButton } from '../../components/ClusterCandidatesButton.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { Select } from '../../components/Select.js';
import { SkeletonRows } from '../../components/Skeleton.js';
import { Button } from '../../components/ui/Button.js';
import { useFullBleed } from '../../lib/layout-width.js';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: CandidateReviewStatus[] = [
  'pending',
  'approved',
  'rejected',
  'superseded',
  'clustered',
];

export function RuleCandidatesPage() {
  useFullBleed();
  const { hasRole } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState<CandidateReviewStatus>('pending');
  const [confidence, setConfidence] = useState<AuthorConfidence | ''>('');
  const [sourceId, setSourceId] = useState('');
  const [author, setAuthor] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<CandidateRule[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sources, setSources] = useState<MediumSource[]>([]);

  // Spec 092 — selection + modal state.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkErr, setBulkErr] = useState<string | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  // Spec 092 — admin gating mirror of backend `requireRuleAdmin`. Backend is
  // the authoritative gate (403 on bypass); this controls only the button's
  // disabled state + tooltip.
  const canBulkReject = hasRole('rule_admin') || hasRole('orbit:admin');

  const offset = page * PAGE_SIZE;

  // Load sources once for the filter dropdown. Failure is non-fatal —
  // the dropdown simply shows "all sources" with no options.
  useEffect(() => {
    let cancelled = false;
    listSources()
      .then((s) => {
        if (!cancelled) setSources(s);
      })
      .catch(() => {
        /* swallow — filter degrades gracefully */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listCandidates({
      status,
      confidence: confidence || undefined,
      author: author.trim() || undefined,
      source_id: sourceId || undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, confidence, sourceId, author, offset, refetchTick]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  // ─── Selection helpers (spec 092) ────────────────────────────
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = rows.some((r) => selectedIds.has(r.id));

  // Set the DOM indeterminate property — React has no JSX attribute for it.
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  // FR-14 — clear selection on filter / page change. We do this by reacting
  // to the same state keys that trigger refetch.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [status, confidence, sourceId, author, offset]);

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllOnPage() {
    if (allOnPageSelected) {
      // Deselect every row on the current page (preserves cross-page selections,
      // though in practice the FR-14 effect clears them on page change anyway).
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const r of rows) next.delete(r.id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const r of rows) next.add(r.id);
        return next;
      });
    }
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function openConfirm() {
    setBulkErr(null);
    setBulkReason('');
    setConfirmOpen(true);
  }

  function cancelConfirm() {
    if (bulkSubmitting) return; // prevent close mid-flight
    setConfirmOpen(false);
    setBulkErr(null);
  }

  async function submitBulkReject() {
    if (bulkSubmitting) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkSubmitting(true);
    setBulkErr(null);
    try {
      const result = await bulkRejectCandidates(ids, bulkReason.trim() || undefined);
      const skipped = result.skipped_ids.length;
      const message =
        skipped > 0
          ? `Rejected ${result.rejected_count} of ${ids.length} — ${skipped} ${
              skipped === 1 ? 'was' : 'were'
            } already reviewed`
          : `Rejected ${result.rejected_count} candidate${result.rejected_count === 1 ? '' : 's'}`;
      toast.addToast(message, 'success');
      setConfirmOpen(false);
      clearSelection();
      setRefetchTick((t) => t + 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setBulkErr(msg);
      toast.addToast(msg, 'error');
    } finally {
      setBulkSubmitting(false);
    }
  }

  const selectionCount = selectedIds.size;

  return (
    <div data-testid="admin-rule-candidates" className="hub-page--table" data-density="compact">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Rule candidates' },
        ]}
      />
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title flex items-center gap-2">
            <Icon name="bx-star" />
            Rule candidates
          </h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
            New rules distilled from community posts awaiting consultant review.
          </p>
        </div>
        {/* Spec 099 / 103 — admin-gated bulk rescan + cluster. Both components
            return null for non-admins so the buttons are absent from the DOM. */}
        <div className="flex items-center gap-2">
          <ClusterCandidatesButton onClusterComplete={() => setRefetchTick((t) => t + 1)} />
          <RescanCandidatesButton onRescanComplete={() => setRefetchTick((t) => t + 1)} />
        </div>
      </div>

      <div className="hub-toolbar">
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Status</span>
          <Select
            data-testid="candidates-filter-status"
            value={status}
            onChange={(v) => {
              setStatus(v as CandidateReviewStatus);
              setPage(0);
            }}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
          />
        </label>
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Confidence</span>
          <Select
            data-testid="candidates-filter-confidence"
            value={confidence}
            onChange={(v) => {
              setConfidence(v as AuthorConfidence | '');
              setPage(0);
            }}
            options={[
              { value: '', label: 'any' },
              { value: 'high', label: 'high' },
              { value: 'medium', label: 'medium' },
              { value: 'low', label: 'low' },
            ]}
          />
        </label>
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Source</span>
          <Select
            data-testid="candidates-filter-source"
            value={sourceId}
            onChange={(v) => {
              setSourceId(v);
              setPage(0);
            }}
            options={[
              { value: '', label: 'all sources' },
              ...sources.map((s) => ({ value: s.id, label: s.identifier })),
            ]}
          />
        </label>
        <label className="text-sm">
          <span className="block text-[var(--hub-color-ink-muted)] mb-1">Author</span>
          <input
            data-testid="candidates-filter-author"
            type="text"
            value={author}
            placeholder="filter by author"
            onChange={(e) => {
              setAuthor(e.target.value);
              setPage(0);
            }}
            className="hub-input"
          />
        </label>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err}
        </div>
      )}

      {/* Spec 092 — bulk action bar. Not sticky: spec 232 spends sticky on
          exactly two things (the bar, a thead), and this page already has a
          sticky thead below. */}
      {selectionCount > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center gap-3 rounded-md border border-[var(--hub-color-line)] bg-[var(--hub-color-action-soft)] px-4 py-2"
        >
          <span className="text-sm font-medium" data-testid="bulk-selection-count">
            {selectionCount} selected
          </span>
          <button
            type="button"
            data-testid="bulk-clear"
            onClick={clearSelection}
            className="text-sm text-[var(--hub-color-action-text)]"
          >
            Clear
          </button>
          <span className="ml-auto" />
          <Button
            variant="danger"
            data-testid="bulk-reject-button"
            onClick={openConfirm}
            disabled={!canBulkReject || bulkSubmitting}
            title={canBulkReject ? undefined : 'Requires rule_admin role'}
          >
            <Icon name="bx-x" /> Reject {selectionCount}
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="hub-table" data-testid="candidates-table">
          <thead>
            <tr>
              <th className="w-8">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  data-testid="bulk-header-checkbox"
                  aria-label="Select all on this page"
                  checked={allOnPageSelected}
                  onChange={toggleSelectAllOnPage}
                  disabled={rows.length === 0}
                />
              </th>
              <th>Criterion</th>
              <th>Author</th>
              <th>Confidence</th>
              <th>Engines</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7}>
                  <SkeletonRows rows={7} cols={5} />
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-[var(--hub-color-ink-muted)]">
                  No candidates match the current filters.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr key={r.id} data-testid={`candidate-row-${r.id}`}>
                  <td>
                    <input
                      type="checkbox"
                      data-testid={`candidate-select-${r.id}`}
                      aria-label={`Select ${r.criterion}`}
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                    />
                  </td>
                  <td className="font-medium">
                    {r.criterion}
                  </td>
                  <td>
                    {r.source_post_author ?? <span className="text-[var(--hub-color-ink-subtle)]">—</span>}
                  </td>
                  <td>
                    <ConfidenceBadge c={r.author_confidence} />
                  </td>
                  <td>
                    {r.engines_mentioned.length > 0 ? r.engines_mentioned.join(', ') : <span className="text-[var(--hub-color-ink-subtle)]">—</span>}
                  </td>
                  <td className="text-xs text-[var(--hub-color-ink-muted)]">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/admin/rules/candidates/${r.id}`}
                      className="text-[var(--hub-color-action-text)]"
                      data-testid={`candidate-details-${r.id}`}
                    >
                      Details
                    </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-[var(--hub-color-ink-muted)]">
        <span>
          Showing {total === 0 ? 0 : offset + 1}-{Math.min(total, offset + rows.length)} of {total}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Prev
          </Button>
          <span>
            Page {page + 1} / {totalPages}
          </span>
          <Button variant="ghost" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      </div>

      {/* Spec 092 — confirm modal */}
      {confirmOpen && (
        <BulkRejectModal
          count={selectionCount}
          reason={bulkReason}
          onReasonChange={setBulkReason}
          submitting={bulkSubmitting}
          error={bulkErr}
          onCancel={cancelConfirm}
          onConfirm={submitBulkReject}
        />
      )}
    </div>
  );
}

function ConfidenceBadge({ c }: { c: AuthorConfidence }) {
  const styles: Record<AuthorConfidence, string> = {
    high: 'bg-emerald-100 text-emerald-800',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-slate-200 text-slate-700',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[c]}`}>
      {c}
    </span>
  );
}

interface BulkRejectModalProps {
  count: number;
  reason: string;
  onReasonChange: (v: string) => void;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function BulkRejectModal({
  count,
  reason,
  onReasonChange,
  submitting,
  error,
  onCancel,
  onConfirm,
}: BulkRejectModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-reject-title"
      data-testid="bulk-reject-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 id="bulk-reject-title" className="text-base font-semibold text-slate-900">
            Reject {count} candidate{count === 1 ? '' : 's'}?
          </h2>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-slate-600">
            The selected candidates will be marked as rejected. The audit trail
            (who rejected, when, why) is preserved.
          </p>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700">
              Reason (optional, ≤500 chars)
            </span>
            <textarea
              data-testid="bulk-reject-reason"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              disabled={submitting}
              placeholder="e.g. duplicates of existing rules"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <span className="block text-xs text-slate-400 mt-1">
              {reason.length}/500
            </span>
          </label>
          {error && (
            <div
              data-testid="bulk-reject-error"
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              {error}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            data-testid="bulk-reject-cancel"
            onClick={onCancel}
            disabled={submitting}
            className="hub-btn hub-btn--ghost"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="bulk-reject-confirm"
            onClick={onConfirm}
            disabled={submitting}
            className="hub-btn hub-btn--danger"
          >
            {submitting ? 'Rejecting…' : `Reject ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}
